import { makeAutoObservable, reaction, runInAction } from 'mobx';
import { agent } from '../api/agent';
import { Activity, ActivityFormValues } from '../models/activity';
import { format } from 'date-fns';
import { store } from './store';
import { Profile } from '../models/profile';
import { Pagination, PagingParams } from '../models/pagination';

class ActivityStore {
  activityRegistry = new Map<string, Activity>();
  selectedActivity: Activity | undefined = undefined;
  editMode = false;
  loading = false;
  loadingInitial = false;
  pagination: Pagination | null = null;
  pagingParams = new PagingParams();
  predicate = new Map().set('all', true);

  constructor() {
    makeAutoObservable(this);

    reaction(
      () => this.predicate.keys(),
      () => {
        this.pagingParams = new PagingParams();
        this.activityRegistry.clear();
        this.loadActivities();
      },
    );
  }

  setPagingParams = (params: PagingParams) => {
    this.pagingParams = params;
  };

  setPredicate = (predicate: string, value: string | Date) => {
    const resetPredicate = () => {
      this.predicate.forEach((_, key) => {
        if (key !== 'startDate') this.predicate.delete(key);
      });
    };

    switch (predicate) {
      case 'all':
        resetPredicate();
        this.predicate.set(predicate, true);
        break;

      case 'isGoing':
        resetPredicate();
        this.predicate.set(predicate, true);
        break;

      case 'isHost':
        resetPredicate();
        this.predicate.set(predicate, true);
        break;

      case 'startDate':
        this.predicate.delete(predicate);
        this.predicate.set(predicate, value);
    }
  };

  get axiosParams() {
    const params = new URLSearchParams();
    params.set('pageNumber', this.pagingParams.pageNumber.toString());
    params.set('pageSize', this.pagingParams.pageSize.toString());

    this.predicate.forEach((value, key) => {
      if (key === 'startDate') {
        params.append(key, (value as Date).toISOString());
      } else {
        params.append(key, value);
      }
    });

    return params;
  }

  private getActivity = (id: string) => {
    return this.activityRegistry.get(id);
  };

  private setActivity = (activity: Activity) => {
    if (!activity.date) throw new Error('Activity date is missing');
    const user = store.userStore.user;
    if (user) {
      activity.isGoing = activity.attendees!.some(
        (aa) => aa.username === user.username,
      );
      activity.isHost = activity.hostUsername === user.username;
      activity.host = activity.attendees?.find(
        (aa) => aa.username === activity.hostUsername,
      );
    }
    activity.date = new Date(activity.date);
    this.activityRegistry.set(activity.id, activity);
  };

  get activitiesByDate() {
    return Array.from(this.activityRegistry.values()).sort((a, b) => {
      if (!a.date || !b.date) throw new Error('Activity date is missing');

      return a.date?.getTime() - b.date?.getTime();
    });
  }

  get groupedActivities() {
    return Object.entries(
      this.activitiesByDate.reduce((activities, activity) => {
        if (!activity.date) throw new Error('Activity date is missing');

        const date = format(activity.date, 'dd MMM yyyy');
        activities[date] = activities[date]
          ? [...activities[date], activity]
          : [activity];
        return activities;
      }, {} as { [key: string]: Activity[] }),
    );
  }

  loadActivities = async () => {
    this.setLoadingInitial(true);
    try {
      const result = await agent.Activities.list(this.axiosParams);
      result.data.forEach((activity) => this.setActivity(activity));
      this.setPagination(result.pagination);
    } catch (error) {
      console.log('Error in loading activities: ', error);
    } finally {
      this.setLoadingInitial(false);
    }
  };

  setPagination = (pagination: Pagination) => {
    this.pagination = pagination;
  };

  loadActivity = async (id: string): Promise<Activity | undefined> => {
    const activity = this.getActivity(id);
    if (activity) {
      this.selectedActivity = activity;
      return activity;
    } else {
      this.setLoadingInitial(true);
      try {
        const activity = await agent.Activities.details(id);
        this.setActivity(activity);
        runInAction(() => (this.selectedActivity = activity));
        this.setLoadingInitial(false);
        return activity;
      } catch (error) {
        console.log('Error in loading single activity: ', error);
        this.setLoadingInitial(false);
      }
    }
  };

  setLoadingInitial = (state: boolean) => {
    this.loadingInitial = state;
  };

  createActivity = async (activity: ActivityFormValues) => {
    const user = store.userStore.user;
    const attendee = new Profile(user!);

    try {
      await agent.Activities.create(activity);
      const newActivity = new Activity(activity);
      newActivity.hostUsername = user!.username;
      newActivity.attendees = [attendee];
      this.setActivity(newActivity);

      runInAction(() => {
        this.selectedActivity = newActivity;
      });
    } catch (error) {
      console.log('Error in creating activity: ', error);
    }
  };

  updateActivity = async (activity: ActivityFormValues) => {
    try {
      await agent.Activities.update(activity);

      runInAction(() => {
        if (activity.id) {
          const updatedActivity = {
            ...this.getActivity(activity.id),
            ...activity,
          };
          this.activityRegistry.set(activity.id, updatedActivity as Activity);
          this.selectedActivity = updatedActivity as Activity;
        }
      });
    } catch (error) {
      console.log('Error in updating activity: ', error);
    }
  };

  deleteActivity = async (id: string) => {
    this.loading = true;
    try {
      await agent.Activities.delete(id);

      runInAction(() => {
        this.activityRegistry.delete(id);
        this.loading = false;
      });
    } catch (error) {
      console.log('Error in deleting activity: ', error);

      runInAction(() => {
        this.loading = false;
      });
    }
  };

  updateAttendance = async () => {
    const user = store.userStore.user;
    if (!user) throw new Error('User is not logged in');
    if (!this.selectedActivity) throw new Error('Activity is not selected');

    this.loading = true;

    try {
      await agent.Activities.attend(this.selectedActivity.id);
      runInAction(() => {
        if (!this.selectedActivity)
          throw new Error('Activity have to be selected');

        if (this.selectedActivity.isGoing) {
          this.selectedActivity.attendees =
            this.selectedActivity.attendees?.filter(
              (aa) => aa.username !== user.username,
            );
          this.selectedActivity.isGoing = false;
        } else {
          const attendee = new Profile(user);
          this.selectedActivity.attendees?.push(attendee);
          this.selectedActivity.isGoing = true;
        }
        this.activityRegistry.set(
          this.selectedActivity.id,
          this.selectedActivity,
        );
      });
    } catch (error) {
      console.error('Error in updating attendance: ', error);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  cancelActivityToggle = async () => {
    this.loading = true;
    try {
      await agent.Activities.attend(this.selectedActivity!.id);
      runInAction(() => {
        this.selectedActivity!.isCancelled =
          !this.selectedActivity?.isCancelled;
        this.activityRegistry.set(
          this.selectedActivity!.id,
          this.selectedActivity!,
        );
      });
    } catch (error) {
      console.error('Error with canceling activity: ', error);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  clearSelectedActivity = () => {
    this.selectedActivity = undefined;
  };

  updateAttendeeFollowing = (username: string) => {
    this.activityRegistry.forEach((activity) => {
      activity.attendees.forEach((attendee) => {
        if (attendee.username === username) {
          attendee.following
            ? attendee.followersCount--
            : attendee.followersCount++;

          attendee.following = !attendee.following;
        }
      });
    });
  };
}

export { ActivityStore };
